import useSWR from 'swr';
import { useMemo } from 'react';

// ==============================|| PRIVACYPAL PORTAL API ||============================== //
//
// All calls are same-origin. The customer-portal Express server proxies /api/* to
// ops-runtime and carries the httpOnly `partnerToken` cookie, so we always send
// `credentials: 'include'`.
//
// Data contract (ops-runtime customer-portal-controller):
//   GET  /api/admin/customers-domains-users -> { success, data: Row[] }
//        Row = { customerName, domain, userEmail, userFullName, userIsAdmin,
//                subscriptionStatus, isPersonal, userStatus, userDisabled,
//                userTier ('pro' | 'max' when userId, else null),
//                customerCreated, userCreated, userId,
//                referralCode, referralSource, referralAttributedAt }
//        referralCode names the reseller or referral partner who sent this
//        COMPANY to us, captured from ?ref= on the Max signup URL and written
//        once per company. It repeats on every user row of that company, so it
//        must never be rendered as a per-user fact. null means direct.
//   GET  /api/admin/api-key-usage?days=N    -> { success, data: UsageRow[] }
//        UsageRow = { domain, companyName, apiKeyId, apiKeyName, keyPrefix,
//                     keyStatus, requests, elements, lastUsedAt }
//   POST /api/admin/users/:id/{disable,enable}
//   GET/POST /api/partner-auth/{me,login,logout}

export async function apiRequest(path, options = {}) {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options
  });
  const text = await res.text();
  let body = {};
  try {
    body = text ? JSON.parse(text) : {};
  } catch {
    body = { raw: text };
  }
  if (!res.ok) {
    // ops-runtime sends `error` as a string; family-api sends { code, message }.
    // Read both, or a family-api refusal surfaces as "[object Object]".
    const apiError = body?.error;
    const message = typeof apiError === 'string' ? apiError : apiError?.message;
    const err = new Error(message || res.statusText || `Request failed (${res.status})`);
    err.status = res.status;
    err.code = apiError && typeof apiError === 'object' ? apiError.code : undefined;
    err.body = body;
    throw err;
  }
  return body;
}

// SWR fetcher — unwraps the { success, data } envelope
const fetcher = (path) => apiRequest(path).then((b) => (b && b.data !== undefined ? b.data : b));

const swrOptions = { revalidateOnFocus: false, keepPreviousData: true, shouldRetryOnError: false };

// ---- Customers / domains / users -------------------------------------------------

export function useCustomersDomainsUsers() {
  const { data, error, isLoading, mutate } = useSWR('/api/admin/customers-domains-users', fetcher, swrOptions);
  return useMemo(() => ({ rows: Array.isArray(data) ? data : [], error, isLoading, mutate }), [data, error, isLoading, mutate]);
}

// ---- API-key usage ---------------------------------------------------------------

export function useApiKeyUsage(days = 30) {
  const { data, error, isLoading, mutate } = useSWR(`/api/admin/api-key-usage?days=${days}`, fetcher, swrOptions);
  return useMemo(() => ({ rows: Array.isArray(data) ? data : [], error, isLoading, mutate }), [data, error, isLoading, mutate]);
}

// ---- Protection usage (DetailedAuditLog aggregate) -------------------------------
//
//   GET /api/admin/protection-usage?days=N -> { totalElements, totalTokensIn,
//        totalTokensOut, totalTokens, sessions, activeUsers, byDate[],
//        byDomain[], byPlatform[] }
// byPlatform: [{ key, name, vendor, sessions, users, elements, tokensIn,
//        tokensOut, tokens }] — provider rollup (Claude/ChatGPT/Gemini/…),
// unknown platform ids land in an 'other' bucket so buckets sum to totals.
// Aggregated across every user on the partner's customers — the same per-prompt
// audit store the proxy app's local stats come from. Sessions cover EVERY AI
// conversation that crossed PrivacyPal, protected or not (usage-only rows).

export function useProtectionUsage(days = 30) {
  const { data, error, isLoading, mutate } = useSWR(`/api/admin/protection-usage?days=${days}`, fetcher, swrOptions);
  return useMemo(() => ({ protection: data || null, error, isLoading, mutate }), [data, error, isLoading, mutate]);
}

// ---- User access control ---------------------------------------------------------

export function disableUser(userId, reason) {
  return apiRequest(`/api/admin/users/${encodeURIComponent(userId)}/disable`, {
    method: 'POST',
    body: JSON.stringify(reason ? { reason } : {})
  });
}

export function enableUser(userId) {
  return apiRequest(`/api/admin/users/${encodeURIComponent(userId)}/enable`, {
    method: 'POST',
    body: JSON.stringify({})
  });
}

// ---- Tier control (EPIC-07) ------------------------------------------------------
//
// Staff no-charge tier override: writes User.tier directly and never touches
// Stripe. This is the only path that changes tier without billing.
//   POST /api/admin/users/:userId/tier   { tier: 'pro' | 'max' } -> { success, data: { id, email, tier } }
//   POST /api/admin/domains/:domain/tier { tier: 'pro' | 'max' } -> { success, data: { domain, tier, updated } }

export function setUserTier(userId, tier) {
  return apiRequest(`/api/admin/users/${encodeURIComponent(userId)}/tier`, {
    method: 'POST',
    body: JSON.stringify({ tier })
  });
}

export function setDomainTier(domain, tier) {
  return apiRequest(`/api/admin/domains/${encodeURIComponent(domain)}/tier`, {
    method: 'POST',
    body: JSON.stringify({ tier })
  });
}

// ---- Families (PrivacyPal Family licensing) --------------------------------------
//
// Proxied by the portal Express server: /api/family-admin/* -> family-api
// /api/family/admin/*, which verifies the same partner JWT. Read-only.
// Signals only: family-api never exposes prompt text, replies, or message
// content to this surface, so nothing rendered here can contain one.
//
//   GET /api/family-admin/summary
//        -> { families, active, trialing, canceled, devices, devicesOnline,
//             openAlerts, estimatedMrrCents, referredFamilies, referredActive,
//             directFamilies, referralPartners }
//   GET /api/family-admin/families?page=&pageSize=&search=&referral=
//        -> { items: FamilyRow[], total, page, pageSize, referral }
//        FamilyRow = { id, name, createdAt, status, trialEnd, memberCount,
//                      kidCount, deviceCount, devicesOnline, openAlerts,
//                      referralCode, referralSource, referralAttributedAt }
//   GET /api/family-admin/families/:id
//        -> { family, members[], devices[], alerts[] }
//   GET /api/family-admin/referrals?limit=
//        -> { items: ReferralRow[], partners, referredFamilies, referredActive,
//             directFamilies, estimatedMrrCents }
//        ReferralRow = { referralCode, families, active, trialing, pastDue,
//                        canceled, estimatedMrrCents, firstReferralAt,
//                        lastReferralAt }
//
// Affiliate attribution. referralCode names the partner, reseller or creator
// who referred a household, captured from ?ref= on the signup URL and written
// once per family. null means a direct signup. `referral` on the families call
// narrows the table to one partner's book; the literal 'none' is the
// complement, every household nobody gets credit for.

export function useFamilySummary() {
  const { data, error, isLoading, mutate } = useSWR('/api/family-admin/summary', fetcher, swrOptions);
  return useMemo(() => ({ summary: data || null, error, isLoading, mutate }), [data, error, isLoading, mutate]);
}

// `page` is 1-based (the API echoes it back); MUI TablePagination is 0-based,
// so callers add 1 on the way in and subtract 1 on the way out.
export function useFamilies({ page = 1, pageSize = 25, search = '', referral = '' } = {}) {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  if (search) params.set('search', search);
  if (referral) params.set('referral', referral);
  const { data, error, isLoading, mutate } = useSWR(`/api/family-admin/families?${params.toString()}`, fetcher, swrOptions);
  return useMemo(
    () => ({
      families: Array.isArray(data?.items) ? data.items : [],
      total: Number(data?.total ?? 0),
      page: Number(data?.page ?? page),
      pageSize: Number(data?.pageSize ?? pageSize),
      error,
      isLoading,
      mutate
    }),
    [data, error, isLoading, mutate, page, pageSize]
  );
}

/** The affiliate book: one row per partner code, with its household counts. */
export function useReferrals({ limit = 200 } = {}) {
  const { data, error, isLoading, mutate } = useSWR(
    `/api/family-admin/referrals?limit=${encodeURIComponent(limit)}`,
    fetcher,
    swrOptions
  );
  return useMemo(
    () => ({
      referrals: Array.isArray(data?.items) ? data.items : [],
      partners: Number(data?.partners ?? 0),
      referredFamilies: Number(data?.referredFamilies ?? 0),
      referredActive: Number(data?.referredActive ?? 0),
      directFamilies: Number(data?.directFamilies ?? 0),
      estimatedMrrCents: Number(data?.estimatedMrrCents ?? 0),
      error,
      isLoading,
      mutate
    }),
    [data, error, isLoading, mutate]
  );
}

export function getFamily(familyId) {
  return apiRequest(`/api/family-admin/families/${encodeURIComponent(familyId)}`);
}

// ---- Family partner trials (EPIC-19) ---------------------------------------------
//
// A company or association gets N Family households on a trial of D days, either
// complimentary or paid for by the partner with the fee credited back to their
// Stripe balance. Administrator-only on the server (requirePartnerAdmin), so the
// nav and the route hide the page from members. Same /api/family-admin proxy.
//
//   GET    /api/family-admin/partner-trials            -> { items: Program[], totals }
//   POST   /api/family-admin/partner-trials            { partnerName, seats, trialDays,
//            billingMode ('complimentary'|'partner_paid'), feeCents?, creditCents?,
//            currency?, partnerContactEmail?, dueDays?, redeemBy?, code?,
//            referralCode?, note? }                     -> 201 { program }
//   GET    /api/family-admin/partner-trials/:id        -> { program, households[], invoices[] }
//   PATCH  /api/family-admin/partner-trials/:id        { status?, note? } -> { program }
//   POST   /api/family-admin/partner-trials/:id/extend        { days } -> { program, extended }
//   POST   /api/family-admin/partner-trials/:id/send-invoice  -> { program }  (Stripe emails it)
//   POST   /api/family-admin/partner-trials/:id/refresh       -> { program }  (re-read from Stripe)
//   POST   /api/family-admin/partner-trials/:id/invoices      { description, amountCents, dueDays? }
//            -> 201 { invoice, program }
//   DELETE /api/family-admin/partner-trials/:id        -> { deleted: true }; 409 once any
//            household redeemed or the fee is paid
//
// `?trial=` is entitlement, never price (`?promo=`) or attribution (`?ref=`).
// households[] is counts and statuses only, the admin-router privacy rule.

export function useFamilyPartnerTrials(enabled = true) {
  const { data, error, isLoading, mutate } = useSWR(enabled ? '/api/family-admin/partner-trials' : null, fetcher, swrOptions);
  return useMemo(
    () => ({
      programs: Array.isArray(data?.items) ? data.items : [],
      totals: data?.totals || null,
      error,
      isLoading,
      mutate
    }),
    [data, error, isLoading, mutate]
  );
}

export function createFamilyPartnerTrial(payload) {
  return apiRequest('/api/family-admin/partner-trials', { method: 'POST', body: JSON.stringify(payload) });
}

export function getFamilyPartnerTrial(id) {
  return apiRequest(`/api/family-admin/partner-trials/${encodeURIComponent(id)}`);
}

export function updateFamilyPartnerTrial(id, fields) {
  return apiRequest(`/api/family-admin/partner-trials/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(fields)
  });
}

export function extendFamilyPartnerTrial(id, days) {
  return apiRequest(`/api/family-admin/partner-trials/${encodeURIComponent(id)}/extend`, {
    method: 'POST',
    body: JSON.stringify({ days })
  });
}

export function sendFamilyPartnerTrialInvoice(id) {
  return apiRequest(`/api/family-admin/partner-trials/${encodeURIComponent(id)}/send-invoice`, {
    method: 'POST',
    body: JSON.stringify({})
  });
}

export function refreshFamilyPartnerTrial(id) {
  return apiRequest(`/api/family-admin/partner-trials/${encodeURIComponent(id)}/refresh`, {
    method: 'POST',
    body: JSON.stringify({})
  });
}

export function invoiceFamilyPartner(id, payload) {
  return apiRequest(`/api/family-admin/partner-trials/${encodeURIComponent(id)}/invoices`, {
    method: 'POST',
    body: JSON.stringify(payload)
  });
}

export function deleteFamilyPartnerTrial(id) {
  return apiRequest(`/api/family-admin/partner-trials/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// ---- Portal users (Settings > Users) ----------------------------------------------
//
// The people who can sign in to THIS portal (:PartnerUser), as distinct from the
// product end users on the Customers page (:User). Administrator-only on the
// server: every call below returns 403 PARTNER_ADMIN_REQUIRED for a member, so
// the UI hides the section rather than rendering it broken.
//
//   GET    /api/admin/portal-users
//            -> { users: PortalUser[], self: { id, email, role }, adminCount }
//          PortalUser = { id, email, firstName, lastName, role ('admin'|'member'),
//                         createdAt, updatedAt, lastLoginAt, passwordUpdatedAt }
//   POST   /api/admin/portal-users             { email, firstName, lastName, role?, password? }
//            -> { id, email, role, password, generated }  (password shown ONCE)
//   POST   /api/admin/portal-users/:id/role     { role }
//   POST   /api/admin/portal-users/:id/password { password? }  -> { password, generated }
//   PATCH  /api/admin/portal-users/:id          { firstName, lastName }
//   DELETE /api/admin/portal-users/:id
//
// Passwords are stored only as bcrypt hashes. The `password` in a create or
// reset response is the single time it is ever readable — there is no endpoint
// that can read one back.

export function usePortalUsers(enabled = true) {
  const { data, error, isLoading, mutate } = useSWR(enabled ? '/api/admin/portal-users' : null, fetcher, swrOptions);
  return useMemo(
    () => ({
      users: Array.isArray(data?.users) ? data.users : [],
      self: data?.self || null,
      adminCount: Number(data?.adminCount ?? 0),
      error,
      isLoading,
      mutate
    }),
    [data, error, isLoading, mutate]
  );
}

export function createPortalUser(payload) {
  return apiRequest('/api/admin/portal-users', { method: 'POST', body: JSON.stringify(payload) });
}

export function setPortalUserRole(userId, role) {
  return apiRequest(`/api/admin/portal-users/${encodeURIComponent(userId)}/role`, {
    method: 'POST',
    body: JSON.stringify({ role })
  });
}

// Omit `password` to have the server generate one.
export function resetPortalUserPassword(userId, password) {
  return apiRequest(`/api/admin/portal-users/${encodeURIComponent(userId)}/password`, {
    method: 'POST',
    body: JSON.stringify(password ? { password } : {})
  });
}

export function updatePortalUserProfile(userId, fields) {
  return apiRequest(`/api/admin/portal-users/${encodeURIComponent(userId)}`, {
    method: 'PATCH',
    body: JSON.stringify(fields)
  });
}

export function deletePortalUser(userId) {
  return apiRequest(`/api/admin/portal-users/${encodeURIComponent(userId)}`, { method: 'DELETE' });
}

// ---- Max trial codes (Trials) ----------------------------------------------------
//
// The vouchers staff issue so a company can evaluate PrivacyPal Max.
// Administrator-only on the server: every call returns 403
// PARTNER_ADMIN_REQUIRED for a member, so the nav and the route hide the
// section rather than rendering it broken.
//
// THREE CODES, NEVER CONFLATED. This is the one that grants ENTITLEMENT:
//   ?promo=  a Stripe coupon. Changes PRICE.
//   ?ref=    a partner code. ATTRIBUTION only, never price.
//   ?trial=  one of these. Changes what the company GETS.
// A prospect can arrive on the Max page with all three at once.
//
// Two kinds, and the only fact the signup branches on is requiresCard:
//   'enterprise'  no card at all. Max granted company-wide for trialDays,
//                 then it drops back to Pro on its own.
//   'stripe'      a card is still collected; Stripe runs the subscription and
//                 the code carries a Stripe promotion code.
//
//   GET    /api/admin/max-trials?limit=N
//            -> { codes: TrialCode[], summary: { total, active, enterprise, stripe, redemptions } }
//          TrialCode = { code, kind ('stripe'|'enterprise'), trialDays, maxSeats,
//                        maxRedemptions, redemptions, redeemedBy, lastRedeemedAt,
//                        stripeCouponId, stripePromotionCodeId, note, status
//                        ('active'|'paused'|'revoked'), expiresAt (epoch ms),
//                        expiresAtIso, createdBy, createdAt, updatedAt }
//   POST   /api/admin/max-trials    { kind, code?, trialDays?, maxSeats?,
//                                     maxRedemptions?, stripePromotionCodeId?,
//                                     stripeCouponId?, note?, expiresAt? }
//            -> TrialCode  (omit `code` and the server generates one)
//   PATCH  /api/admin/max-trials/:code  { status, note? }
//   DELETE /api/admin/max-trials/:code
//   GET    /api/admin/max-trials/:code/redemptions
//            -> { code: TrialCode, redemptions: Redemption[] }
//          Redemption = { companyId, companyName, domain, trialEndsAt,
//                         subscriptionStatus, redeemedAt }

export function useMaxTrials(enabled = true) {
  const { data, error, isLoading, mutate } = useSWR(enabled ? '/api/admin/max-trials' : null, fetcher, swrOptions);
  return useMemo(
    () => ({
      codes: Array.isArray(data?.codes) ? data.codes : [],
      summary: data?.summary || null,
      error,
      isLoading,
      mutate
    }),
    [data, error, isLoading, mutate]
  );
}

export function issueMaxTrial(payload) {
  return apiRequest('/api/admin/max-trials', { method: 'POST', body: JSON.stringify(payload) });
}

export function setMaxTrialStatus(code, status, note) {
  return apiRequest(`/api/admin/max-trials/${encodeURIComponent(code)}`, {
    method: 'PATCH',
    body: JSON.stringify(note === undefined ? { status } : { status, note })
  });
}

export function deleteMaxTrial(code) {
  return apiRequest(`/api/admin/max-trials/${encodeURIComponent(code)}`, { method: 'DELETE' });
}

export function getMaxTrialRedemptions(code) {
  return apiRequest(`/api/admin/max-trials/${encodeURIComponent(code)}/redemptions`);
}

// ---- Partner auth ----------------------------------------------------------------

export function partnerMe() {
  return apiRequest('/api/partner-auth/me');
}

// `platformEndpoint` (optional) signs in against a different PrivacyPal
// Platform; the portal server persists it as the new default only after the
// login succeeds against it.
export function partnerLogin(email, password, platformEndpoint) {
  const payload = { email, password };
  if (platformEndpoint) payload.platformEndpoint = platformEndpoint;
  return apiRequest('/api/partner-auth/login', {
    method: 'POST',
    body: JSON.stringify(payload)
  });
}

export function partnerLogout() {
  return apiRequest('/api/partner-auth/logout', { method: 'POST', body: JSON.stringify({}) }).catch(() => ({}));
}

// ---- Service health (Status page) ------------------------------------------------

export function useProxyHealth() {
  const { data, error, isLoading } = useSWR(
    '/api/admin/proxy-test',
    (path) => apiRequest(path).catch((e) => ({ ok: false, error: e.message })),
    { revalidateOnFocus: false, refreshInterval: 30000, shouldRetryOnError: false }
  );
  return { health: data, error, isLoading };
}

// Measured platform status: the portal server probes the configured platform's
// control plane, product API, family API, and graph database on a schedule and
// returns real statuses, latencies, and observed uptime.
//   GET /api/admin/platform-status -> { platformEndpoint, checkedAt, windowStart,
//        services: [{ id, name, meta, status, latencyMs, medianMs, detail,
//                     uptimePct, samples, windowStart }],
//        series: [{ id, name, points: [{ t, ms }] }] }
export function usePlatformStatus() {
  const { data, error, isLoading, mutate } = useSWR('/api/admin/platform-status', fetcher, {
    revalidateOnFocus: false,
    refreshInterval: 30000,
    shouldRetryOnError: false,
    keepPreviousData: true
  });
  return useMemo(() => ({ status: data || null, error, isLoading, mutate }), [data, error, isLoading, mutate]);
}

// ---- Portal configuration & settings ---------------------------------------------
//
// Served by the portal Express server itself (not proxied):
//   GET /api/portal-config    (public)  -> { partnerName, platformEndpoint, platformEndpointSource }
//   GET /api/portal-settings  (partner) -> full settings, secrets masked (••••1234)
//   PUT /api/portal-settings  (partner) -> persists to the settings file and
//        merge-syncs changed keys into the deployed AWS secrets

export function getPortalConfig() {
  return apiRequest('/api/portal-config').then((b) => (b && b.data !== undefined ? b.data : b));
}

export function usePortalConfig() {
  const { data, error, isLoading } = useSWR('/api/portal-config', fetcher, swrOptions);
  return useMemo(() => ({ config: data || null, error, isLoading }), [data, error, isLoading]);
}

// Administrators only on the server. `enabled` lets the Settings page skip the
// request entirely for a member instead of firing a request it knows is a 403.
export function usePortalSettings(enabled = true) {
  const { data, error, isLoading, mutate } = useSWR(enabled ? '/api/portal-settings' : null, fetcher, swrOptions);
  return useMemo(() => ({ settings: data || null, error, isLoading, mutate }), [data, error, isLoading, mutate]);
}

export function updatePortalSettings(partial) {
  return apiRequest('/api/portal-settings', { method: 'PUT', body: JSON.stringify(partial) });
}

// ---- Billing (Stripe-backed, proxied to the product API) -------------------------
//
// Partner-authenticated:
//   GET  /api/billing/portal/summary  -> { portfolio, customers[] } (real Stripe data)
//   GET  /api/billing/portal/products -> { products[] } catalog for invoice creation
//   POST /api/billing/portal/links    -> create a secure payment link
//   GET  /api/billing/portal/links    -> list payment links
//   GET  /api/billing/portal/customers/:id/invoices -> Stripe invoice history
// Public (payment page, UUID capability):
//   GET  /api/billing/public/links/:id
//   POST /api/billing/public/links/:id/pay
//   POST /api/billing/public/links/:id/finalize

export function useBillingSummary() {
  const { data, error, isLoading, mutate } = useSWR('/api/billing/portal/summary', fetcher, swrOptions);
  return useMemo(() => ({ summary: data || null, error, isLoading, mutate }), [data, error, isLoading, mutate]);
}

export function useBillingProducts() {
  const { data, error, isLoading } = useSWR('/api/billing/portal/products', fetcher, swrOptions);
  return useMemo(() => ({ products: data?.products || [], error, isLoading }), [data, error, isLoading]);
}

export function usePaymentLinks() {
  const { data, error, isLoading, mutate } = useSWR('/api/billing/portal/links', fetcher, swrOptions);
  return useMemo(() => ({ links: Array.isArray(data) ? data : [], error, isLoading, mutate }), [data, error, isLoading, mutate]);
}

export function createPaymentLink(payload) {
  return apiRequest('/api/billing/portal/links', { method: 'POST', body: JSON.stringify(payload) });
}

export function cancelPaymentLink(linkId) {
  return apiRequest(`/api/billing/portal/links/${encodeURIComponent(linkId)}/cancel`, {
    method: 'POST',
    body: JSON.stringify({})
  });
}

export function getCustomerInvoices(companyId) {
  return apiRequest(`/api/billing/portal/customers/${encodeURIComponent(companyId)}/invoices`);
}

// Public payment page (no partner session required)
export function getPublicPaymentLink(linkId) {
  return apiRequest(`/api/billing/public/links/${encodeURIComponent(linkId)}`);
}

export function payPublicPaymentLink(linkId, payload) {
  return apiRequest(`/api/billing/public/links/${encodeURIComponent(linkId)}/pay`, {
    method: 'POST',
    body: JSON.stringify(payload)
  });
}

export function finalizePublicPaymentLink(linkId, payload) {
  return apiRequest(`/api/billing/public/links/${encodeURIComponent(linkId)}/finalize`, {
    method: 'POST',
    body: JSON.stringify(payload)
  });
}

// E-sign the Master Subscription Agreement for a payment link (clickwrap).
// Server records name, email, timestamp, IP, geo-location, and MSA version.
export function acceptMsaPublicPaymentLink(linkId, payload) {
  return apiRequest(`/api/billing/public/links/${encodeURIComponent(linkId)}/accept-msa`, {
    method: 'POST',
    body: JSON.stringify(payload)
  });
}

// Validate a Stripe coupon/promotion code against a payment link's order.
// Returns coupon details + the discounted total for the link's seats.
export function validatePromoPublicPaymentLink(linkId, promoCode) {
  return apiRequest(`/api/billing/public/links/${encodeURIComponent(linkId)}/validate-promo`, {
    method: 'POST',
    body: JSON.stringify({ promoCode })
  });
}
